import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { useMessagesContext } from '../../../context/MessagesContext';
import { useAuth } from '../../../context/AuthContext';
import { useApiBase } from '../../../hooks/useApiBase';
import { useLogEvent } from '../../../hooks/useLogEvent';
import { editMessage, deleteMessage, addReaction, removeReaction } from '../../../api/activities';
import { getRecipientsFromConversation, getReactionToggleAction } from '../../../utils/messageUtils';
import { handleApiError } from '../../../utils/errorUtils';

interface ConversationThreadProps {
  conversationId: string;
  onBack: () => void;
}

export function ConversationThread({ conversationId, onBack }: ConversationThreadProps) {
  const { currentConversation, sendMessage, fetchConversationMessages, loading, error } = useMessagesContext();
  const { user } = useAuth();
  const apiBase = useApiBase();
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const viewportRef = useRef<HTMLDivElement>(null);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await fetchConversationMessages(conversationId);
    } catch (err) {
      // Error handled by hook
    } finally {
      setRefreshing(false);
    }
  }, [conversationId, fetchConversationMessages]);

  useEffect(() => {
    setMessageText('');
    setSending(false);
    setRefreshing(false);
  }, [conversationId]);

  useEffect(() => {
    // Auto-scroll to bottom when new messages arrive
    if (viewportRef.current) {
      viewportRef.current.scrollTop = viewportRef.current.scrollHeight;
    }
  }, [currentConversation?.messages]);

  const handleSend = useCallback(async () => {
    if (!messageText.trim() || !currentConversation) return;

    const recipients = getRecipientsFromConversation(currentConversation, user?.id);
    if (recipients.length === 0) {
      return;
    }

    setSending(true);
    try {
      // sendMessage already refreshes conversations and current conversation
      await sendMessage(recipients, messageText.trim(), conversationId);
      setMessageText('');
      // No need to manually refresh - sendMessage handles it
    } catch (err) {
      // Error handled by hook
    } finally {
      setSending(false);
    }
  }, [messageText, currentConversation, conversationId, user?.id, sendMessage]);


  const logger = useLogEvent('messages');

  const handleEditMessage = useCallback(
    async (messageId: string, newText: string) => {
      try {
        await editMessage(apiBase, messageId, newText);
        await fetchConversationMessages(conversationId);
      } catch (err) {
        handleApiError(err, 'edit message', logger.logError);
        throw err;
      }
    },
    [apiBase, conversationId, fetchConversationMessages, logger]
  );

  const handleDeleteMessage = useCallback(
    async (messageId: string) => {
      try {
        await deleteMessage(apiBase, messageId);
        await fetchConversationMessages(conversationId);
      } catch (err) {
        handleApiError(err, 'delete message', logger.logError);
        throw err;
      }
    },
    [apiBase, conversationId, fetchConversationMessages, logger]
  );

  const handleReaction = useCallback(
    async (messageId: string, reaction: 'thumbs_up' | 'thumbs_down' | 'question' | 'laugh') => {
      if (!currentConversation) return;
      
      const message = currentConversation.messages.find((m) => m.id === messageId);
      if (!message) return;

      const action = getReactionToggleAction(message, reaction, user?.id);
      
      if (action === 'remove') {
        await removeReaction(apiBase, messageId, reaction);
      } else {
        await addReaction(apiBase, messageId, reaction);
      }
      await fetchConversationMessages(conversationId);
    },
    [apiBase, conversationId, fetchConversationMessages, currentConversation, user?.id]
  );

  if (!currentConversation || currentConversation.conversation_id !== conversationId) {
    return (
      <div className="flex items-center justify-center h-[400px]">
        {loading ? (
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        ) : error ? (
          <div role="alert"><p className="text-sm text-destructive">{error}</p><Button onClick={handleRefresh}>Retry</Button><Button variant="outline" onClick={onBack}>Back to Messages</Button></div>
        ) : (
          <p className="text-sm text-muted-foreground">Loading conversation...</p>
        )}
      </div>
    );
  }

  const messages = currentConversation.messages || [];

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between gap-2 px-2 py-1 border-b">
        <Button variant="ghost" size="icon" onClick={onBack} aria-label="Back to conversations">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" onClick={handleRefresh} disabled={refreshing || loading} aria-label="Refresh conversation">
          {refreshing || loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
        </Button>
      </div>
              <div className="flex-1 overflow-y-auto" ref={viewportRef}>
                <MessageList
                  messages={messages}
                  currentUserId={user?.id}
                  onEdit={handleEditMessage}
                  onDelete={handleDeleteMessage}
                  onReaction={handleReaction}
                />
              </div>
              <MessageInput
                messageText={messageText}
                onMessageTextChange={setMessageText}
                onSend={handleSend}
                sending={sending}
              />
    </div>
  );
}
